import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const TaskDifficultyEnum = z.enum(['Easy', 'Intermediate', 'Hard', 'Expert'])
const Task = z
  .object({
    name: z.string().min(3).max(200),
    body: z.string().min(10).max(50000),
    teaser: z.string().min(10).max(200),
    ownerId: z
      .string()
      .min(1)
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/)
      .optional(),
    language: z.enum(['SQL', 'Python']),
    difficulty: TaskDifficultyEnum,
    description: z.string().min(20).max(10000),
    defaultQuery: z.string().min(1).max(2000),
    seedFactor: z.number(),
    feedbackPayload: z.string().min(2).optional(),
    dataBank: z.string().min(2),
    accessGroups: z
      .array(
        z
          .string()
          .min(1)
          .max(50)
          .regex(/^[a-zA-Z0-9_-]+$/)
      )
      .min(1)
      .max(10),
    proficiencies: z
      .array(
        z.enum([
          'joins',
          'dataSanitization',
          'anomalyDetection',
          'timeSeriesAnalysis',
          'windowFunctions',
          'aggregation',
        ])
      )
      .optional(),
    solutionPlaceholder: z.string().optional(),
    availabilityWeight: z.number().optional(),
    deleted: z.boolean().optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    slug: z.string().optional(),
  })
  .passthrough()
  .and(z.object({ id: z.string() }).passthrough())
const FeedbackPersonalInfo = z
  .object({ yearsOfXP: z.number().gte(0).lte(10) })
  .partial()
  .passthrough()
const FeedbackUser = z
  .object({ id: z.string(), personalInfo: FeedbackPersonalInfo.optional() })
  .passthrough()
const SubmitSolutionRequest = z
  .object({
    challengeId: z.string(),
    task: Task,
    solution: z.string(),
    user: FeedbackUser,
    insightsAudience: z.enum(['school', 'employer', 'none']).optional(),
    is_employer_request: z.boolean().optional(),
    feedback_language: z
      .enum(['en', 'he', 'es', 'fr', 'de'])
      .optional()
      .default('en'),
  })
  .passthrough()
const Feedback = z
  .object({
    user_body: z.string(),
    user_grade: z.number().int().gte(1).lte(10),
    org_feedback_md: z.string().nullish(),
    org_grade: z.number().int().gte(1).lte(10).nullish(),
  })
  .passthrough()
const ErrorResponse = z
  .object({
    error: z.string(),
    code: z.string(),
    details: z.object({}).partial().passthrough(),
  })
  .partial()
  .passthrough()
const FeedbackStatusEnum = z.enum([
  'unprocessed',
  'processing1',
  'processing2',
  'processing3',
  'processing4',
  'processing5',
  'processing6',
  'processing7',
  'processing8',
  'processing9',
  'processing10',
  'processing11',
  'processing12',
  'processing13',
  'processing14',
  'processing15',
  'processed',
])
const FeedbackResultResponse = z
  .object({ status: FeedbackStatusEnum, feedback: Feedback.optional() })
  .passthrough()

export const schemas = {
  TaskDifficultyEnum,
  Task,
  FeedbackPersonalInfo,
  FeedbackUser,
  SubmitSolutionRequest,
  Feedback,
  ErrorResponse,
  FeedbackStatusEnum,
  FeedbackResultResponse,
}

const endpoints = makeApi([
  {
    method: 'post',
    path: '/feedback',
    alias: 'submitSolution',
    description: `Submit a solution for a challenge task`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SubmitSolutionRequest,
      },
    ],
    response: Feedback,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid input data`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/feedback-result/:challenge_id',
    alias: 'getFeedbackResult',
    description: `Get the feedback result for a submitted solution`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'challenge_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: FeedbackResultResponse,
    errors: [
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
])

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
